import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertToArrays = (obj: any) => {
  const arrays: { [key: string]: string } = {};
  for (const prop in obj) {
    if (typeof obj[prop] === "object") {
      arrays[prop] = Object.entries(obj[prop])
        .filter(([_, value]) => value === true)
        .map(([key, _]) => key)
        .join(",");
    }
  }
  return arrays;
};

export function formateStringCount(inputStr: string, count = 2) {
  if (!inputStr || typeof inputStr !== "string") return "";

  const roles = inputStr
    .split(",")
    .map((role) => role.trim())
    .filter((role) => role);

  if (roles.length <= count) {
    return roles.join(", ");
  }

  const firstThree = roles.slice(0, count);
  const remainingCount = roles.length - count;

  // return `${firstThree.join(", ")} +${remainingCount}`;
  return `${firstThree.join(", ")} & more`;
}

export function formatDate(dateString: string) {
  const date: any = new Date(dateString);
  const now: any = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  // If date is invalid
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  // Within 24 hours
  if (diffInSeconds < 86400) {
    if (diffInSeconds < 60) {
      return `${Math.max(diffInSeconds, 0)} sec${diffInSeconds > 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hr${hours > 1 ? "s" : ""} ago`;
    }
  }

  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return "Yesterday";
  }

  // Format as dd/mm/yyyy
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}
export function formatDateMMDDYY(dateString: string) {
  const date: any = new Date(dateString);
  const now: any = new Date();
  // Format as dd/mm/yyyy
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

export function formatSmartDate(input: string | Date): string {
  const now = new Date();
  const date = new Date(input);

  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

export const formateErrorMessage = (data: any) => {
  return data?.response?.data?.message ?? "Something went wrong";
};

export const removeFalsyValues = (
  obj: any,
  removeEmptyObjects = false,
): any => {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => removeFalsyValues(item, removeEmptyObjects))
      .filter(
        (item) =>
          !(
            removeEmptyObjects &&
            typeof item === "object" &&
            Object.keys(item).length === 0
          ),
      );
  }

  const result: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      // Recursively clean nested objects/arrays
      if (typeof value === "object" && value !== null) {
        const cleanedValue = removeFalsyValues(value, removeEmptyObjects);

        // Skip if the cleaned object is empty and removeEmptyObjects is true
        if (
          removeEmptyObjects &&
          typeof cleanedValue === "object" &&
          Object.keys(cleanedValue).length === 0
        ) {
          continue;
        }

        result[key] = cleanedValue;
      }
      // Keep the value if it's truthy
      else if (value) {
        result[key] = value;
      }
      // // Special case for numbers to keep negative numbers
      // else if (typeof value === "number" && value !== 0) {
      //   result[key] = value;
      // }
    }
  }

  return result;
};

export const generateSelectOptions = (arr: string[]) => {
  return arr.map((data) => {
    return {
      label: data,
      value: data,
    };
  });
};

// → "June 29, 2025" (or current date)
export function formatFullDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;

  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function convertBoldMarkdown(input: string) {
  return input.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

export function formatCurrency(amount: string | number): string {
  if (amount === null || amount === undefined) return "";

  const num = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(num)) return "";

  return num.toLocaleString("en-IN"); // en-IN for Indian-style numbering (80,000)
}
